import { PlusOutlined } from "@ant-design/icons"
import { COLOR } from "@finway-group/shared/lib/consts/colors"
import { UnitOfMeasureEnums } from "@finway-group/shared/lib/consts/lineItems"
import { CostCenter, CostCenter2, Expense, ExpenseAccount, PriceSourceEnum, Tax } from "@finway-group/shared/lib/models"
import { LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { LINE_ITEM_QUANTITY_MAX, LINE_ITEM_QUANTITY_MIN_BIGGER_THAN } from "@finway-group/shared/lib/models/purchaseOrder/lineItem.validation"
import { Button, Col, Collapse, Divider, Form, Input, InputNumber, Row, Select, Tooltip } from "antd"
import { FormInstance, FormListOperation } from "antd/lib/form"
import { FormListFieldData } from "antd/lib/form/FormList"
import CurrencyInput from "Components/currencyInput"
import { FormSelectOption } from "Components/form/form.types"
import { createSplitRules } from "Components/forms/rules/splitRules"
import { ExpandableListItemIcon } from "Components/icons/ExpandableListItemIcon"
import { LinkIcon } from "Components/icons/LinkIcon"
import { ExpenseData } from "Components/modals/expenseCreateForm.context"
import { createLineItemRules } from "Features/pages/purchaseOrders/components/LineItem.rules"
import React, { useMemo, useState } from "react"
import { Plus as PlusIcon, Trash2 as TrashIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { NOT_SET_VALUE } from "Shared/config/consts"
import { useCompany } from "Shared/hooks/company.hooks"
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import { useIsExpenseAccountMandatory, useItemsHook } from "Shared/hooks/expense.hooks"
import { useExpenseAccounts } from "Shared/hooks/expenseAccount.hooks"
import { checkIsExpenseAccountMandatoryForExpenseStatus, isFolderExpense } from "Shared/utils/expense.utils"
import { getDefaultTaxId } from "Shared/utils/helper.utils"
import { GetPopupAnchor } from "Shared/utils/popup.utils"
import { DebouncedState } from "use-debounce/lib/useDebouncedCallback"
import { CostCenter2Select } from "./CostCenter2Select"
import { CostCenterSelect } from "./CostCenterSelect"
import { ExpenseAccountSelect } from "./ExpenseAccountSelect"
import FormItemCurrency from "./formItemCurrency"
import FormTaxRateSelector from "./formTaxRateSelector"

interface LineItemListProps {
    expense: Expense
    taxValues: Array<Tax>
    onNetPriceUpdate: (index: number, netPrice: number) => void
    onTaxRateSplitUpdate: (index: number, taxRateId: string, priceSource: PriceSourceEnum) => void
    onTaxPriceUpdate: (index: number, taxPrice: number, priceSource: PriceSourceEnum) => void
    onGrossPriceUpdate: (index: number, grossPrice: number) => void
    getPopupAnchor: GetPopupAnchor
    expenseForm: FormInstance
    updateExpense(data: ExpenseData): void
    isLinked: boolean
    isForcedSync: boolean
}

const ExpandIcon = ({ isActive }: { isActive?: boolean }) => <ExpandableListItemIcon expanded={isActive ?? false} />

export const LineItemList = (props: LineItemListProps) => {
    const { expense, expenseForm, updateExpense, isLinked, isForcedSync } = props
    const { onNameChange, onFieldUpdate, onQuantityUpdate, onUnitPriceUpdate, onAddLineItem, onRemoveLineItem } = useItemsHook(
        expense,
        expenseForm,
        updateExpense,
        isLinked,
        isForcedSync,
    )
    const { t } = useTranslation()
    const costCenters = useSubCostCenters(true)
    const { showCostCentersIds } = useCompany()
    const isExpenseAccountMandatory = useIsExpenseAccountMandatory()
    const shouldShowExpenseAccount = checkIsExpenseAccountMandatoryForExpenseStatus({
        expenseStatus: expense.status,
        isExpenseAccountMandatory,
        isFolder: isFolderExpense(expense),
    })
    const rules = createSplitRules({ expense, expenseForm })
    const expenseAccounts = useExpenseAccounts()
    const costCenters2 = useCostCenters2()
    const removeLineItem = (formRemove: FormListOperation["remove"]) => (index: number) => {
        formRemove(index)
        onRemoveLineItem(index)
    }
    const unitOfMeasureOptions: Array<FormSelectOption> = useMemo(
        () =>
            Object.values(UnitOfMeasureEnums).map((enumValue) => ({
                param: t(`enum:UnitOfMeasureEnums.${enumValue}`),
                value: enumValue,
            })),
        [t],
    )

    return (
        <Form.List name="splits">
            {(fields, { add, remove }) => (
                <div style={{ marginBottom: 16 }} className="line-item-list">
                    {fields.length > 0 && <div className="text-sm font-medium">{t("input:request.line_items")}</div>}
                    {/* In oneTimeExpense form, we only want to show the item form if the expense split type is ITEM. */}
                    {fields.map((field, index) => (
                        <LineItem
                            key={field.key}
                            {...props}
                            {...{
                                onNameChange,
                                onFieldUpdate,
                                onQuantityUpdate,
                                onUnitPriceUpdate,
                                field,
                                index,
                                onRemoveLineItem: removeLineItem(remove),
                                defaultOpen: index === 0,
                                showCostCentersIds,
                                costCenters,
                                shouldShowExpenseAccount,
                                expenseAccounts,
                                unitOfMeasureOptions,
                                rules,
                                costCenters2,
                            }}
                        />
                    ))}
                    {
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => onAddLineItem(add)}>
                            {t("action:request.item.add")}
                        </Button>
                    }
                </div>
            )}
        </Form.List>
    )
}

interface LineItemProps extends LineItemListProps {
    onNameChange: (name: string, index: number) => void
    onFieldUpdate: DebouncedState<(index: number, field: string, value: any, additionalCB?: (field: string) => void) => void>
    onQuantityUpdate: (index: number, quantity: number) => void
    onUnitPriceUpdate: (index: number, unitPrice: number) => void
    field: FormListFieldData
    index: number
    onRemoveLineItem: (index: number) => void
    defaultOpen: boolean
    showCostCentersIds: boolean
    costCenters: Array<CostCenter>
    shouldShowExpenseAccount: boolean
    expenseAccounts: Array<ExpenseAccount>
    rules: ReturnType<typeof createSplitRules>
    costCenters2: Array<CostCenter2>
    unitOfMeasureOptions: Array<FormSelectOption>
}

const LineItem = ({
    expense,
    taxValues,
    onNameChange,
    onFieldUpdate,
    onQuantityUpdate,
    onUnitPriceUpdate,
    getPopupAnchor,
    onTaxRateSplitUpdate,
    onNetPriceUpdate,
    onTaxPriceUpdate,
    onGrossPriceUpdate,
    rules,
    field,
    index,
    onRemoveLineItem,
    defaultOpen,
    showCostCentersIds,
    costCenters,
    shouldShowExpenseAccount,
    expenseAccounts,
    costCenters2,
    unitOfMeasureOptions,
}: LineItemProps) => {
    const { t } = useTranslation()
    const [priceSource, setPriceSource] = useState(PriceSourceEnum.GROSS_PRICE)
    const [customUnitOfMeasureOptions, setCustomUnitOfMeasureOptions] = useState<Array<FormSelectOption>>(unitOfMeasureOptions)
    const [customUnitOfMeasure, setCustomUnitOfMeasure] = useState<string>("")

    const lineItemRules = createLineItemRules()

    const { currency, splits } = expense
    const split = splits[index] as LineItem
    const indexNumber = (index + 1).toString().padStart(2, "0")
    const selectedExpenseAccountId = split?.expenseAccount?._id || ""
    const selectedCostCenter2Id = split?.costCenter2 || ""

    const isDisabled = !!split?.purchaseOrderLink
    const renderUnitOfMeasureDropdown = (menu: React.ReactElement<any, string | React.JSXElementConstructor<any>>) => (
        <>
            {menu}
            <Divider className="my-6" />
            <Input.Group className="flex flex-row space-x-4" compact>
                <Input value={customUnitOfMeasure} onChange={(e) => setCustomUnitOfMeasure(e.target.value)} />
                <Button
                    onClick={() => {
                        setCustomUnitOfMeasureOptions((prev) => [...prev, { param: customUnitOfMeasure, value: customUnitOfMeasure }])
                        setCustomUnitOfMeasure("")
                    }}
                >
                    <PlusIcon />
                </Button>
            </Input.Group>
        </>
    )
    const firstRow = (
        <Row gutter={12} className="mb-1" onClick={(e) => e.stopPropagation()}>
            <Col span={2}>
                <Form.Item className="mb-0 bg-gray-150 rounded-full h-34">
                    <Input className="border-0 text-center" value={indexNumber} size="small" disabled readOnly />
                </Form.Item>
            </Col>
            <Col span={4}>
                <Form.Item name={[field.name, "materialCode"]} className="mb-0 bg-gray-150 rounded-full h-34" rules={rules.materialCode}>
                    <Input
                        size="small"
                        onChange={(e) => onFieldUpdate.callback(index, "materialCode", e.target.value)}
                        className="border-0 shadow-none outline-none"
                        placeholder={t("placeholder:request.material_code")}
                    />
                </Form.Item>
            </Col>
            <Col span={16}>
                <Form.Item name={[field.name, "name"]} rules={rules.name} className="mb-0">
                    <Input
                        prefix={<span style={{ color: "#FF0000" }}>*</span>}
                        size="small"
                        onChange={(e) => onNameChange(e.target.value, index)}
                        placeholder={t("input:request.item_name")}
                        className="border-0 pl-6"
                    />
                </Form.Item>
            </Col>
            <Col span={2}>
                <Form.Item className="mb-0">
                    {isDisabled ? (
                        <Tooltip title={t("label:po_link.item_linked_to_purchase_order", { po_number: split?.purchaseOrderLink?.purchaseOrderNumber })}>
                            <LinkIcon color={COLOR.finway.green} />
                        </Tooltip>
                    ) : (
                        <Button
                            type="text"
                            shape="circle"
                            icon={<TrashIcon />}
                            onClick={(e) => {
                                e.stopPropagation()
                                onRemoveLineItem(index)
                            }}
                            className={`flex items-center justify-center cursor-pointer`}
                        />
                    )}
                </Form.Item>
            </Col>
        </Row>
    )
    const secondRow = (
        <Row gutter={16} align="bottom">
            <Col span={4}>
                <Form.Item name={[field.name, "quantity"]} label={t("input:request.quantity")} initialValue={1} required rules={rules.quantity}>
                    <InputNumber
                        type="number"
                        placeholder={t("placeholder:request.quantity")}
                        min={LINE_ITEM_QUANTITY_MIN_BIGGER_THAN}
                        max={LINE_ITEM_QUANTITY_MAX}
                        onChange={(value) => onQuantityUpdate(index, value || 0)}
                        size="small"
                        style={{ height: 28 }}
                        disabled={isDisabled}
                    />
                </Form.Item>
            </Col>
            <Col span={4}>
                <Form.Item label={t("input:request.unit_of_measure")} name={[field.name, "unitOfMeasure"]} rules={lineItemRules.unitOfMeasure} initialValue={split?.unitOfMeasure}>
                    <Select
                        getPopupContainer={getPopupAnchor()}
                        dropdownRender={renderUnitOfMeasureDropdown}
                        onSelect={(val) => {
                            onFieldUpdate.callback(index, "unitOfMeasure", val)
                        }}
                        dropdownMatchSelectWidth={false}
                    >
                        {customUnitOfMeasureOptions.map((item) => (
                            <Select.Option value={item.value} key={item.value}>
                                {item.param}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            </Col>
            <Col span={4}>
                <FormItemCurrency
                    currency={currency}
                    name={[field.name, "unitPrice"]}
                    label={t("input:request.unit_price")}
                    key="unitPrice"
                    required={true}
                    rules={rules.unitPrice}
                >
                    <CurrencyInput
                        currency={currency}
                        value={split?.unitPrice ?? 0}
                        onChange={(value) => onUnitPriceUpdate(index, value)}
                        onKeyDown={() => {
                            setPriceSource(PriceSourceEnum.NET_PRICE)
                        }}
                        isDisabled={isDisabled}
                    />
                </FormItemCurrency>
            </Col>
            <Col span={4}>
                <FormTaxRateSelector
                    elem={{
                        name: [field.name, "taxRate", "_id"],
                        label: t("input:request.tax_rate"),
                        key: "taxRate",
                        value: split?.taxRate?._id ?? getDefaultTaxId(taxValues),
                        isRequired: expense.isAtLeastApproved(),
                        className: "w-full text-xs md:text-base no-padding-select no-right-padding-select",
                        getPopupAnchor: getPopupAnchor(),
                        onChange: (value: string) => onTaxRateSplitUpdate(index, value, priceSource),
                        taxValues,
                        isDisabled,
                    }}
                    span={24}
                />
            </Col>
            <Col span={4}>
                <FormItemCurrency currency={currency} name={[field.name, "netPrice"]} label={t("input:request.net_price")} required={true} rules={rules.netPrice}>
                    <CurrencyInput
                        currency={currency}
                        value={0}
                        onChange={(value) => onNetPriceUpdate(index, value)}
                        onKeyDown={() => {
                            setPriceSource(PriceSourceEnum.NET_PRICE)
                        }}
                        isDisabled={isDisabled}
                    />
                </FormItemCurrency>
            </Col>
            <Col span={4}>
                <FormItemCurrency currency={currency} name={[field.name, "grossPrice"]} label={t("input:request.gross_price")} required rules={rules.grossPrice}>
                    <CurrencyInput
                        currency={currency}
                        value={0}
                        onChange={(value) => onGrossPriceUpdate(index, value)}
                        onKeyDown={() => {
                            setPriceSource(PriceSourceEnum.GROSS_PRICE)
                        }}
                        isDisabled={isDisabled}
                    />
                </FormItemCurrency>
            </Col>
        </Row>
    )

    const thirdRow = (
        <Row gutter={16} align="top">
            <Col span={5}>
                <FormItemCurrency currency={currency} name={[field.name, "taxPrice"]} label={t("input:request.tax_price")} rules={rules.taxPrice}>
                    <CurrencyInput currency={currency} value={0} onChange={(value) => onTaxPriceUpdate(index, value, PriceSourceEnum.NET_PRICE)} isDisabled={isDisabled} />
                </FormItemCurrency>
            </Col>
            {expenseAccounts?.length > 0 && (
                <Col span={6}>
                    <Form.Item name={[field.name, "expenseAccount", "_id"]} label={t("input:request.expense_account")} rules={rules.expenseAccount ?? []} preserve required={false}>
                        <ExpenseAccountSelect
                            value={selectedExpenseAccountId}
                            expenseAccounts={expenseAccounts}
                            onSelect={(val) => {
                                onFieldUpdate.callback(index, "expenseAccount", { _id: val })
                            }}
                            showNotSetOption={!shouldShowExpenseAccount}
                        />
                    </Form.Item>
                </Col>
            )}
            <Col span={6}>
                <Form.Item label={t("input:request.cost_center")} name={[field.name, "costCenter"]} required rules={rules.costCenter}>
                    <CostCenterSelect
                        costCenters={costCenters}
                        showCostCentersIds={showCostCentersIds}
                        onSelect={(val) => onFieldUpdate.callback(index, "costCenter", val)}
                        getPopupAnchor={getPopupAnchor}
                    />
                </Form.Item>
            </Col>
            {costCenters2.length > 0 && (
                <Col span={6}>
                    <Form.Item label={t("label:cost_center_2")} name={[field.name, "costCenter2"]}>
                        <CostCenter2Select
                            costCenters2={costCenters2}
                            getPopupAnchor={getPopupAnchor}
                            onSelect={(val) => onFieldUpdate.callback(index, "costCenter2", val)}
                            value={selectedCostCenter2Id || NOT_SET_VALUE}
                        />
                    </Form.Item>
                </Col>
            )}
        </Row>
    )

    return (
        <div key={field.key} className="border border-[#EAE9EF] mb-16" style={{ borderRadius: "6px" }}>
            <Collapse ghost expandIcon={ExpandIcon} bordered defaultActiveKey={defaultOpen ? [index] : undefined}>
                <Collapse.Panel key={index} header={firstRow}>
                    {secondRow}
                    {thirdRow}
                </Collapse.Panel>
            </Collapse>
        </div>
    )
}
